import React, { useState, useEffect } from 'react';
import { Box, Grid, CircularProgress, Alert, Table, TableHead, TableRow, TableCell, TableBody, Typography } from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import BatchChip from '../../components/BatchChip';
import { DetailRow } from '../../components/InvField';
import { getOpeningStock, getStores } from '../../api/stockLedger';
import useItemNames from '../../hooks/useItemNames';
import { formatDate, formatDateTime } from '../../utils';
import { storeDisplayName } from '../../utils/storeEntity';
import { IPD_COLORS } from '../../theme';

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.data || data?.items || []);
}

function lineTotal(line) {
  const qty = Number(line.quantity);
  const val = Number(line.unit_value);
  if (!(qty > 0) || !(val >= 0)) return 0;
  return qty * val;
}

export default function OpeningStockDetail() {
  const { id } = useParams();
  const history = useHistory();
  const { resolveItem } = useItemNames();
  const [record, setRecord] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const [r, storesData] = await Promise.all([
          // API: GET /inventory/stock/opening-stock/{id}/
          getOpeningStock(id),
          // API: GET /inventory/stores/
          getStores().catch(() => []),
        ]);
        if (!active) return;
        setRecord(r);
        setStores(normalizeList(storesData));
      } catch (e) {
        if (active) setError(e.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]);

  function resolveStoreName(storeId) {
    if (!storeId) return null;
    const store = stores.find(s => (s.store_id || s.id) === storeId);
    return store ? storeDisplayName(store) : storeId;
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress sx={{ color: IPD_COLORS.primary }} /></Box>;
  if (error) return <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>;
  if (!record) return null;

  const lines = normalizeList(record.lines);
  const grandTotal = lines.reduce((sum, line) => sum + lineTotal(line), 0);

  return (
    <Box>
      <PageHeader eyebrow="Inventory Management › Opening Stock" title="Opening Stock Entry"
        actions={<InvButton variant="outlined" tone="teal" size="small" onClick={() => history.goBack()}>Back</InvButton>}
      />
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={5}>
          <SectionCard title="Details" actions={record.status ? <StatusChip status={record.status} /> : null}>
            <DetailRow label="Store">{resolveStoreName(record.store_id)}</DetailRow>
            <DetailRow label="Date">{formatDate(record.opening_date)}</DetailRow>
            <DetailRow label="Remarks">{record.remarks}</DetailRow>
            <DetailRow label="Created">{record.created_at ? formatDateTime(record.created_at) : '—'}</DetailRow>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={7}>
          <SectionCard title="Items">
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Item</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Qty</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Batch</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Value / Unit</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Total Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.length === 0
                    ? <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', color: IPD_COLORS.textSecondary }}>No items</TableCell></TableRow>
                    : lines.map((line, i) => (
                      <TableRow key={line.id ?? i}>
                        <TableCell>{resolveItem(line)}</TableCell>
                        <TableCell align="right">{line.quantity}</TableCell>
                        <TableCell><BatchChip batchNumber={line.batch_number} expiryDate={line.expiry_date} /></TableCell>
                        <TableCell align="right">{line.unit_value != null ? Number(line.unit_value).toFixed(2) : '—'}</TableCell>
                        <TableCell align="right">{lineTotal(line).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
            {lines.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1.5 }}>
                <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13, mr: 1 }}>Grand Total:</Typography>
                <Typography sx={{ color: IPD_COLORS.textPrimary, fontSize: 14, fontWeight: 700 }}>{grandTotal.toFixed(2)}</Typography>
              </Box>
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
